import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getCategory, categories } from "@/mocks/categories";
import { articles, getArticle } from "@/mocks/articles";
import { usePageSeo } from "@/hooks/usePageSeo";
import { ArticleCard } from "@/components/feature/ArticleCard";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const cat = getCategory(slug);
  const [order, setOrder] = useState("new");

  const list = useMemo(() => {
    const own = articles.filter((a) => a.cat === slug);
    const sorted = [...own];
    if (order === "hot") sorted.sort((a, b) => b.heat - a.heat);
    else sorted.sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
    return sorted;
  }, [slug, order]);

  usePageSeo({
    title: cat ? `${cat.name} - 吃瓜现场 | 免费在线看热点八卦资讯` : "栏目未找到",
    description: cat
      ? `${cat.desc} 吃瓜现场${cat.name}频道免下载网页在线看，手机版电脑版实时浏览，每日更新热点八卦与资讯。`
      : "没有找到对应的内容频道。",
  });

  if (!cat) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-28 text-center">
        <i className="ri-map-pin-2-line text-5xl text-background-400" aria-hidden="true"></i>
        <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">这个频道不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的频道可能已调整，去看看其他热门栏目吧。</p>
        <Link to="/" className="mt-6 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
          返回首页
        </Link>
      </div>
    );
  }

  const cover = getArticle(slug)?.cover ?? articles[0].cover;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
      {/* 面包屑 */}
      <nav className="flex items-center gap-1.5 text-sm text-foreground-600" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-700">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
        <span className="text-foreground-950">{cat.name}</span>
      </nav>

      {/* 频道横幅 */}
      <section className="relative mt-5 overflow-hidden rounded-3xl">
        <img
          src={cover}
          alt={`${cat.name}频道横幅`}
          title={`${cat.name} - 吃瓜现场`}
          className="h-44 w-full object-cover md:h-56"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/15" aria-hidden="true"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="px-6 md:px-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-background-100/80">{cat.en}</p>
            <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-background-50 md:text-4xl">{cat.name}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-100/90">{cat.tagline} · 免下载网页在线看，实时更新</p>
          </div>
        </div>
      </section>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground-600">
          共 <span className="font-semibold text-primary-600">{cat.count}</span> 篇内容 · 每日持续上新
        </p>
        <div className="flex w-fit items-center gap-1 rounded-full bg-background-100 p-1">
          {[
            { key: "new", label: "最新发布" },
            { key: "hot", label: "热度优先" },
          ].map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => setOrder(o.key)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                order === o.key ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {list.length ? (
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((a) => (
            <ArticleCard key={a.slug} article={a} />
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 py-20 text-center">
          <i className="ri-file-list-3-line text-4xl text-background-400" aria-hidden="true"></i>
          <p className="mt-3 text-sm text-foreground-600">该频道内容正在赶来的路上，先去别的频道逛逛吧。</p>
        </div>
      )}

      {/* 其他频道 */}
      <div className="mt-10 rounded-2xl bg-background-100/60 p-5 md:p-6">
        <h2 className="font-heading text-base font-black text-foreground-950">继续逛逛其他频道</h2>
        <div className="mt-4 flex flex-wrap gap-2.5">
          {categories
            .filter((c) => c.slug !== cat.slug)
            .map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:bg-accent-100 hover:text-accent-800"
              >
                {c.name}
                <span className="text-xs text-foreground-500">{c.count}</span>
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
}